import { useEffect, useState } from "react";
import { ArrowLeft, ChefHat, XCircle } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { usePOS } from "@/context/POSContext";
import { fmtQty } from "@/lib/qty";
import { LoadingState, EmptyState } from "@/components/ui/StateBoundaries";
import { NetworkIndicator } from "@/components/captain/NetworkIndicator";
import { useCaptainNetwork } from "@/hooks/useCaptainNetwork";
import { getOrderItems, listActiveOrders } from "@/lib/orderApi";
import { meshTablesPath } from "@/lib/captainMesh/routes";
import { findLiveAggregateForTable, getOrderMappingRow, serverOrderOnTable } from "@/lib/captainMesh/db";
import CancelItemDialog from "@/components/billing/CancelItemDialog";

const timeOf = iso =>
  iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—";

/**
 * KOT-wise history for one table's running order, newest first.
 *
 * The kitchen's truth: KOT numbers and their sent-at timestamps are the
 * server's (§39.1 — only the server issues KOTs), so the batches come from
 * GET /orders/{id}/items grouped by kot_number. Lines this device captured but
 * the server has not accepted yet appear as one honest "Not sent yet" batch on
 * top — never as a numbered KOT.
 */
export default function CaptainKots() {
  const { tableId } = useParams();
  const navigate = useNavigate();
  const { token, tables } = usePOS();
  const { isOnline } = useCaptainNetwork();

  const table = tables?.find(t => t.id === parseInt(tableId) || t.id === tableId);
  const [state, setState] = useState({ phase: "loading", kots: [], pending: [] });
  // Cancel Item on a sent line (server-only: the KOT and its VOID slip are the
  // server's). Bumping `reload` re-reads the KOTs after a cancel.
  const [voidLine, setVoidLine] = useState(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let stale = false;
    (async () => {
      if (!table) return;

      // Local capture first: unsent lines exist only in this device's fold.
      // The LIVE capture for the table, newest first — not the first row with
      // this table id, which was often an earlier party's finished order and
      // put its KOTs and "Not sent yet" lines on tonight's table.
      const localAgg = await findLiveAggregateForTable(table.id);
      const mapRow = localAgg ? await getOrderMappingRow(localAgg.aggregate_id) : null;
      const localLines = Object.values(localAgg?.state?.items || {}).filter(
        i => i.status !== "cancelled" && i.status !== "removed"
      );
      // A line is unsent until the server acknowledged a KOT for it — the
      // device's own truth, never inferred from what the server holds (a second
      // round of the same dish is not "already sent" because the first was).
      const pending = localLines.filter(l => !l.kot_acked);
      // What the server said about them on the last flush, in its words.
      const heldBack = pending.length ? (mapRow?.held_back || []) : [];

      if (!isOnline || !token) {
        if (!stale) setState({ phase: localAgg ? "ready" : "offline-empty", kots: [], pending, heldBack });
        return;
      }

      try {
        const board = await listActiveOrders(token);
        const orderId = serverOrderOnTable(board, table.id)?.id ?? null;
        // The capture's unsent lines belong here only if it is not synced yet,
        // or it is synced to THIS order.
        const mine = !!localAgg && (!mapRow?.order_id || mapRow.order_id === orderId);
        const localPending = mine ? pending : [];
        if (!orderId) {
          if (!stale) setState({ phase: localPending.length ? "ready" : "no-order", kots: [], pending: localPending, heldBack: localPending.length ? heldBack : [] });
          return;
        }
        const items = await getOrderItems(orderId, token);
        const byKot = new Map();
        const serverUnfired = [];

        for (const item of items) {
          if (item.kot_number) {
            if (!byKot.has(item.kot_number)) byKot.set(item.kot_number, { number: item.kot_number, at: item.kot_sent_at, lines: [] });
            const batch = byKot.get(item.kot_number);
            batch.lines.push({ ...item });
            if (item.status !== "cancelled" && item.kot_sent_at && (!batch.at || item.kot_sent_at < batch.at)) batch.at = item.kot_sent_at;
          } else if (item.status !== "cancelled") {
            serverUnfired.push({ ...item });
          }
        }
        // This device's unsent lines when it has any; otherwise what the
        // server holds un-fired (punched on the till or another pad). Never
        // both: a synced-but-unfired local line IS one of the server's rows.
        const reconciledPending = localPending.length ? localPending : serverUnfired;

        const kots = [...byKot.values()].sort((a, b) => b.number - a.number);
        for (const k of kots) k.lines.sort((a, b) => (a.status === "cancelled") - (b.status === "cancelled") || a.id - b.id);
        if (!stale) setState({ phase: "ready", kots, pending: reconciledPending, orderId, heldBack: localPending.length ? heldBack : [] });
      } catch (error) {
        // Name what failed: "unreachable" was printed for every error, even
        // while the header said Connected (2026-10-31).
        const status = error?.response?.status;
        const degraded = status
          ? `The server answered ${status} while reading this table's KOTs — showing this device's capture only.`
          : error?.response === undefined && error?.request
            ? "Server unreachable — showing this device's capture only."
            : `Could not read this table's KOTs (${error?.message || error}) — showing this device's capture only.`;
        if (!stale) setState({ phase: "ready", kots: [], pending, heldBack, degraded });
      }
    })();
    return () => { stale = true; };
  }, [table, token, isOnline, reload]);

  // State lives in the header band, not a coloured side stripe (DESIGN.md §2.5): a sent
  // KOT is the resting state and stays neutral; "not sent yet" is the one that needs
  // attention, so only it is tinted (warning token) — and it says so in words.
  const Batch = ({ title, time, lines, tone, cancellable }) => (
    <section className={`bg-card border rounded-xl shadow-sm overflow-hidden ${tone === "pending" ? "border-warning/40" : ""}`}>
      <div className={`px-4 py-2.5 flex items-center justify-between gap-3 border-b ${tone === "pending" ? "bg-warning-subtle text-warning" : "bg-muted/40"}`}>
        <strong className={`text-body font-bold flex items-center gap-2 ${tone === "pending" ? "" : "text-foreground"}`}>
          <ChefHat className="w-4 h-4" />{title}
        </strong>
        <span className="text-micro font-bold uppercase text-muted-foreground tabular-nums">{time}</span>
      </div>
      <div className="divide-y">
        {lines.map((l, i) => {
          const cancelled = l.status === "cancelled";
          return (
            <div className={`px-4 py-2.5 flex items-center gap-3 ${cancelled ? "opacity-60" : ""}`} key={l.id ?? l.line_id ?? i} data-testid={cancelled ? "kot-line-cancelled" : "kot-line"}>
              <b className={`w-9 shrink-0 tabular-nums text-body ${cancelled ? "line-through" : ""}`}>{fmtQty(l.quantity)}×</b>
              <span className={`flex-1 text-body text-foreground truncate ${cancelled ? "line-through" : ""}`}>{l.item_name}</span>
              {cancelled && <span className="text-micro font-bold uppercase text-destructive">Cancelled</span>}
              {cancellable && !cancelled && (
                <button
                  className="w-12 h-12 -mr-2 flex items-center justify-center rounded-full text-danger hover:bg-danger-subtle"
                  onClick={() => setVoidLine(l)}
                  aria-label={`Cancel ${l.item_name}`}
                  data-testid="cancel-item-button"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );

  return (
    <div className="flex flex-col h-full bg-background" data-testid="captain-kots-screen">
      <header className="min-h-[3.5rem] pt-safe flex items-center justify-between px-3 bg-primary text-primary-foreground shadow-md shrink-0">
        <div className="flex items-center gap-2">
          <button
            className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full hover:bg-primary-foreground/20"
            aria-label="Back to tables"
            onClick={() => navigate(meshTablesPath())}
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex flex-col">
            <small className="text-micro font-bold opacity-80 uppercase">KOT history</small>
            <h2 className="text-body font-bold leading-tight">{table?.name}</h2>
          </div>
        </div>
        <NetworkIndicator />
      </header>

      <div className="flex-1 overflow-y-auto p-base space-y-snug">
        {state.phase === "loading" && <LoadingState message="Reading KOTs…" />}
        {state.phase === "no-order" && <EmptyState title="No running order" message="This table has nothing in the kitchen." />}
        {state.phase === "offline-empty" && (
          <EmptyState title="Offline" message="KOT history lives on the server; nothing was captured on this device." />
        )}

        {state.phase === "ready" && (
          <>
            {state.pending.length > 0 && (
              <Batch
                title="Not sent yet"
                time="pending"
                lines={state.pending}
                tone="pending"
              />
            )}
            {state.pending.length > 0 && state.heldBack?.length > 0 && (
              <p className="text-micro text-warning" data-testid="kot-held-back-reason">
                Server held these back: {state.heldBack.join("; ")}. Use Sync &amp; reload in the menu to send again.
              </p>
            )}
            {state.kots.map(k => (
              <Batch
                key={k.number}
                title={`KOT ${k.number}`}
                time={timeOf(k.at)}
                lines={k.lines}
                tone="sent"
                cancellable={isOnline && !!state.orderId}
              />
            ))}
            {state.degraded && (
              <p className="text-micro text-muted-foreground text-center">{state.degraded}</p>
            )}
            {!state.pending.length && !state.kots.length && (
              <EmptyState title="No KOTs yet" message="Nothing has been fired for this table." />
            )}
          </>
        )}
      </div>

      {voidLine && state.orderId && (
        <CancelItemDialog
          orderId={state.orderId}
          line={voidLine}
          token={token}
          onDone={(_, { orderCancelled } = {}) => {
            setVoidLine(null);
            if (orderCancelled) return navigate(meshTablesPath());
            setReload(n => n + 1);
          }}
          onClose={() => setVoidLine(null)}
        />
      )}
    </div>
  );
}
